import { api } from './api';

export type AllocationStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'conference_requested'
  | 'withdrawn'
  // Counsel has asked to return instructions but is STILL ON RECORD until a clerk
  // records the return — the label must not imply they have already come off.
  | 'return_requested'
  | 'returned'
  | 'superseded'
  | 'completed';

export type DeclineReason = 'diary_clash' | 'conflict_of_interest' | 'other';

export type ConferencePurpose =
  | 'screening'
  | 'case_prep'
  | 'court_day'
  | 'settlement'
  | 'post_trial'
  | 'other';

export type ConferenceFormat = 'in_chambers' | 'at_court' | 'video' | 'phone';

export interface AllocationCase {
  id: string;
  caseNumber: string;
  caseName: string;
  caseType: string;
  status: string;
  description?: string | null;
  feeEstimate?: string | number | null;
  feeType?: string | null;
  practiceArea?: { id: string; name: string } | null;
  court?: { id: string; name: string; shortName: string } | null;
  layClient?: { id: string; firstName: string; lastName: string; company: string | null } | null;
  instructingSolicitor?: { id: string; firstName: string; lastName: string; firmName: string | null; email: string | null } | null;
  opponentParty?: string | null;
  opponentCounsel?: string | null;
  hearings?: Array<{ id: string; hearingDate: string; hearingType: string; status: string }>;
}

export interface AllocationBarrister {
  id: string;
  seniority: number;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    memberType: string;
  };
}

export interface AllocationConference {
  id: string;
  purpose: ConferencePurpose;
  layClientAttending: boolean;
  format: ConferenceFormat;
  scheduledAt: string | null;
  proposedDates: string[] | null;
  durationMinutes: number;
  notes: string | null;
}

export interface Allocation {
  id: string;
  status: AllocationStatus;
  caseId: string;
  barristerId: string;
  offeredById: string;
  offeredAt: string;
  respondedAt: string | null;
  withdrawnAt: string | null;
  clerkNote: string | null;
  barristerNote: string | null;
  declineReason: DeclineReason | null;
  declineReasonOther: string | null;
  requestedConferenceId: string | null;
  respondByDate: string | null;
  reminderSentAt: string | null;
  escalatedAt: string | null;
  /** Which seat this counsel occupies. Only the lead seat is exclusive. */
  role: 'lead' | 'junior' | 'second_counsel' | 'specialist';
  /** The engagement this allocation serves. Null on rows predating the Instruction model. */
  instructionId: string | null;
  /** The allocation this one replaces — the chain that makes a reassignment provable. */
  previousAllocationId: string | null;
  returnGround: string | null;
  returnNarrative: string | null;
  returnedAt: string | null;
  case: AllocationCase;
  barrister: AllocationBarrister;
  offeredBy: { id: string; firstName: string; lastName: string; email: string };
  requestedConference: AllocationConference | null;
}

// ─── Clerk endpoints ────────────────────────────────────────

export async function offerAllocation(payload: {
  caseId: string;
  barristerId: string;
  clerkNote?: string;
}): Promise<Allocation> {
  const { data } = await api.post('/allocations', payload);
  return data.data;
}

export interface ListAllocationsParams {
  status?: AllocationStatus;
  caseId?: string;
  barristerId?: string;
  page?: number;
  limit?: number;
}

export interface ListAllocationsResult {
  data: Allocation[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// Shared config for polled endpoints — tells the browser never to
// serve a cached response. Without this, 304s cause stale empty state.
const NO_CACHE = { headers: { 'Cache-Control': 'no-store', Pragma: 'no-cache' } };

export async function listAllocations(params?: ListAllocationsParams): Promise<ListAllocationsResult> {
  const { data } = await api.get('/allocations', { ...NO_CACHE, params });
  // The list endpoint returns { data: rows, pagination: { ... } } at the top level
  return data as ListAllocationsResult;
}

export async function withdrawAllocation(id: string, reason?: string): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${id}/withdraw`, reason ? { reason } : {});
  return data.data;
}

export async function listAwaiting(): Promise<Allocation[]> {
  const { data } = await api.get('/allocations/awaiting', NO_CACHE);
  return data.data;
}

export async function listForCase(caseId: string): Promise<Allocation[]> {
  const { data } = await api.get('/allocations', { ...NO_CACHE, params: { caseId, limit: 50 } });
  return data.data;
}

/** A commitment already in the barrister's diary that touches the slot being staffed. */
export interface ClashingCommitment {
  id: string;
  title: string;
  eventType: string;
  startAt: string;
  endAt: string;
  kind: 'overlap' | 'adjacent';
  gapMinutes?: number;
}

/** 'unknown' = could not be computed. It never means "clear". */
export type CandidateDiaryVerdict = 'clear' | 'adjacent' | 'overlap' | 'unknown';
export type CandidateConflictVerdict = 'clear' | 'potential' | 'critical' | 'unknown';

export interface CandidateBarrister {
  id: string;
  seniority: number;
  callYear: number | null;
  silkYear: number | null;
  availability: string;
  openOffersCount: number;
  /** Date the verdicts below were computed against (null = none resolvable). */
  workDate: string | null;
  durationMinutes: number | null;
  diaryVerdict: CandidateDiaryVerdict;
  clashingCommitments: ClashingCommitment[];
  conflictVerdict: CandidateConflictVerdict;
  conflictReasons: string[];
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    memberType: string;
  };
}

export async function listCandidateBarristers(
  caseId?: string,
  opts?: { workDate?: string; durationMinutes?: number },
): Promise<CandidateBarrister[]> {
  const { data } = await api.get('/allocations/candidates', {
    params: {
      ...(caseId ? { caseId } : {}),
      ...(opts?.workDate ? { workDate: opts.workDate } : {}),
      ...(opts?.durationMinutes ? { durationMinutes: opts.durationMinutes } : {}),
    },
  });
  return data.data;
}

// ─── Barrister endpoints ────────────────────────────────────

export async function listInbox(): Promise<Allocation[]> {
  const { data } = await api.get('/allocations/inbox', NO_CACHE);
  return data.data;
}

export async function acceptAllocation(
  id: string,
  barristerNote?: string,
): Promise<Allocation> {
  const { data } = await api.post(
    `/allocations/${id}/accept`,
    barristerNote ? { barristerNote } : {},
  );
  return data.data;
}

export async function declineAllocation(
  id: string,
  payload: { declineReason: DeclineReason; declineReasonOther?: string; barristerNote?: string },
): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${id}/decline`, payload);
  return data.data;
}

export async function requestConference(
  id: string,
  payload: {
    purpose: ConferencePurpose;
    layClientAttending: boolean;
    format: ConferenceFormat;
    proposedDates?: string[];
    durationMinutes?: number;
    barristerNote?: string;
  },
): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${id}/request-conference`, payload);
  return data.data;
}

export async function directApproveAllocation(
  id: string,
  note: string,
): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${id}/direct-approve`, { note });
  return data.data;
}

export async function directDeclineAllocation(
  id: string,
  reason: string,
): Promise<void> {
  await api.post(`/allocations/${id}/direct-decline`, { reason });
}

// Re-propose conference dates (barrister-side, before clerk confirms).
export async function addProposedDates(
  conferenceId: string,
  proposedDates: string[],
): Promise<void> {
  await api.post(`/conferences/${conferenceId}/proposed-dates`, { proposedDates });
}

// ─── Display helpers ────────────────────────────────────────

export const STATUS_LABEL: Record<AllocationStatus, string> = {
  pending: 'Awaiting response',
  accepted: 'Accepted',
  declined: 'Declined',
  conference_requested: 'Conference requested',
  withdrawn: 'Withdrawn',
  return_requested: 'Return requested',
  returned: 'Returned',
  superseded: 'Replaced',
  completed: 'Completed',
};

export const DECLINE_REASON_LABEL: Record<DeclineReason, string> = {
  diary_clash: 'Diary clash',
  conflict_of_interest: 'Conflict of interest',
  other: 'Other',
};

export const PURPOSE_LABEL: Record<ConferencePurpose, string> = {
  screening: 'Screening',
  case_prep: 'Case preparation',
  court_day: 'Court-day',
  settlement: 'Settlement / ADR',
  post_trial: 'Post-trial',
  other: 'Other',
};

export const FORMAT_LABEL: Record<ConferenceFormat, string> = {
  in_chambers: 'In chambers',
  at_court: 'At court',
  video: 'Video',
  phone: 'Phone',
};

export function describeConference(c: Pick<AllocationConference, 'purpose' | 'format' | 'layClientAttending'>): string {
  return `${PURPOSE_LABEL[c.purpose]} · ${FORMAT_LABEL[c.format]} · ${c.layClientAttending ? 'with lay client' : 'no lay client'}`;
}

// Defaults for the request-conference form (matches the locked design)
export const CONFERENCE_DEFAULTS: Record<
  ConferencePurpose,
  { layClientAttending: boolean; format: ConferenceFormat }
> = {
  screening: { layClientAttending: false, format: 'phone' },
  case_prep: { layClientAttending: true, format: 'in_chambers' },
  court_day: { layClientAttending: true, format: 'at_court' },
  settlement: { layClientAttending: true, format: 'in_chambers' },
  post_trial: { layClientAttending: false, format: 'phone' },
  other: { layClientAttending: false, format: 'in_chambers' },
};

// ─── Return of instructions (rC25 / rC26 / rC27) ────────────
//
// Two steps, deliberately separate. Requesting a return does NOT take counsel off the
// record — the allocation moves to `return_requested` and they remain instructed until a
// clerk records the return, which is the gate where the professional client's consent or
// a clear explanation is evidenced. The UI must not collapse these into one button.

export type ReturnGround =
  | 'professional_embarrassment'
  | 'diary_unavailable'
  | 'illness_or_injury'
  | 'bereavement'
  | 'pregnancy_or_childbirth'
  | 'client_request'
  | 'other';

/** Which grounds the Handbook treats as MANDATORY. Display only — the API enforces it. */
export const RETURN_GROUND_LABEL: Record<ReturnGround, string> = {
  professional_embarrassment: 'Professional embarrassment (rC21 — mandatory)',
  diary_unavailable: 'Hearing fixed on an unavailable date (rC26(3))',
  illness_or_injury: 'Illness or injury',
  bereavement: 'Bereavement',
  pregnancy_or_childbirth: 'Pregnancy or childbirth',
  client_request: 'Client request',
  other: 'Other',
};

export async function requestReturn(
  allocationId: string,
  payload: { returnGround: ReturnGround; returnNarrative: string },
): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${allocationId}/request-return`, payload);
  return data.data;
}

export async function recordReturn(
  allocationId: string,
  payload: { consentRecordId?: string; explanationGiven?: boolean; clerkNote?: string },
): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${allocationId}/record-return`, payload);
  return data.data;
}

export async function completeAllocation(allocationId: string, note?: string): Promise<Allocation> {
  const { data } = await api.post(`/allocations/${allocationId}/complete`, note ? { note } : {});
  return data.data;
}
